import clsx from 'clsx';
import React, { useRef, useCallback, MutableRefObject } from 'react';
import type { Swiper } from 'swiper/types';
import type { KeyboardMethods } from 'swiper/types/modules/keyboard';
import { useSwiper } from 'swiper/react';
import { Button } from '@marriott/mi-ui-library';
import { CarouselSwiper, CarouselSlideWrapper } from './Carousel.styles';
import { CarouselBaseProps, CarouselButtonProps, CarouselSlideProps } from './Carousel.types';
import { getClickTrackValue } from '../../utils/defaultTrackingUtils';
import { useCarouselContext } from './Carousel.context';
import { useViewportCategory } from '../../utils/hooks/useViewportCategory';
import { useImageLoadTracker } from '../../hooks';
import 'swiper/css';
import 'swiper/css/pagination';

const nonFocusableSlideA11yKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'];

export const CarouselSlide = React.forwardRef<HTMLDivElement, CarouselSlideProps>(
  ({ children, className, onClick, shouldSetIsInitialized }, _ref) => {
    const { isInitialized } = useCarouselContext();
    const internalRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement | null>;
    const areImagesLoaded = useImageLoadTracker(internalRef, [children]);

    return (
      <CarouselSlideWrapper
        ref={internalRef}
        className={clsx(
          className,
          !areImagesLoaded && 'skeleton-loader',
          shouldSetIsInitialized && !isInitialized && 'swiper-slide-active'
        )}
        onClick={onClick}
      >
        {children}
      </CarouselSlideWrapper>
    );
  }
);

export const CarouselButton = ({
  className,
  direction,
  onClick,
  trackingProperties,
  trackingPropertyFallbacks,
  tabIndex,
}: CarouselButtonProps) => {
  const swiper = useSwiper();
  const isPrevious = direction === 'left';
  const arrowClassName = isPrevious ? 'carousel-left-arrow' : 'carousel-right-arrow';
  const customClickTrackValue = getClickTrackValue({
    trackingProperties,
    fallbacks: trackingPropertyFallbacks,
  });

  return (
    <Button
      callback={() => {
        onClick?.();
        // Set lastKey to prevent focus changes in onSlideChange that cause layout shifts
        // This matches the behavior of keyboard navigation
        if (swiper.keyboard) {
          const keyboardMeta = swiper.keyboard as KeyboardMethods & { lastKey: string | null };
          keyboardMeta.lastKey = isPrevious ? 'ArrowLeft' : 'ArrowRight';
        }
        isPrevious ? swiper.slidePrev() : swiper.slideNext();
      }}
      className={clsx(className, arrowClassName)}
      custom_click_track_value={customClickTrackValue}
      testId={arrowClassName}
      ariaLabel={arrowClassName}
      tabIndex={tabIndex}
      trackingProperties={trackingProperties}
    >
      <span className={isPrevious ? 'icon-arrow-left' : 'icon-arrow-right'} />
    </Button>
  );
};

export function CarouselBase({ children, className, options, onTouchEnd, onSwiper }: CarouselBaseProps) {
  const { setSwiperTouched, setIsInitialized, setIsAtStart, setIsAtEnd, swiperRef } = useCarouselContext();
  const { modules = [] } = options || {};
  const viewportCategory = useViewportCategory(); // This is added to rerender the carousel when width changes - there is a bug that can show when pagination dots need to be added and are not previously there.
  const progressRef = useRef(0);
  const observerSyncedRef = useRef(false);

  const syncEdgeState = useCallback(
    (swiper: Swiper) => {
      setIsAtStart(swiper.isBeginning);
      setIsAtEnd(swiper.isEnd);
    },
    [setIsAtStart, setIsAtEnd]
  );

  return (
    <CarouselSwiper
      observer={true}
      observeParents={true}
      onTouchEnd={(...touchEndArguments: [swiper: Swiper, event: MouseEvent | TouchEvent | PointerEvent]) => {
        setSwiperTouched(() => true);
        onTouchEnd?.(...touchEndArguments);
      }}
      onSwiper={(swiper: Swiper) => {
        swiperRef.current = swiper;
        setIsInitialized(() => true);
        onSwiper?.();
      }}
      onObserverUpdate={(swiper: Swiper) => {
        if (!observerSyncedRef.current) {
          observerSyncedRef.current = true;
          requestAnimationFrame(() => {
            swiper.update();
            syncEdgeState(swiper);
          });
        }
      }}
      onProgress={(_: Swiper, progress: number) => {
        progressRef.current = progress;
      }}
      slidesPerView="auto"
      centeredSlides
      modules={[...modules]}
      keyboard={{ enabled: true, onlyInViewport: true }} // Note: defaults for keyboard module
      className={className}
      key={viewportCategory}
      onSlideChange={(swiper: Swiper) => {
        syncEdgeState(swiper);
        const activeSlide = swiper.slides[swiper.activeIndex];
        const firstFocusableElement = activeSlide.querySelector<HTMLElement>(
          'button, a, input, [tabindex]:not([tabindex="-1"])'
        );
        const keyboardMeta = swiper.keyboard as KeyboardMethods & { lastKey: string | null };
        const lastKey = keyboardMeta?.lastKey;

        if (!nonFocusableSlideA11yKeys.includes(lastKey || '')) {
          firstFocusableElement?.focus();
        }

        if (lastKey) {
          keyboardMeta.lastKey = null;
        }
      }}
      {...options}
    >
      {children}
    </CarouselSwiper>
  );
}
